import { createElement, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import rehypeSanitize from 'rehype-sanitize';
import { useRemarkSync } from 'react-remark';
import { normalizePublicLandingUrl } from '../publicLandingUrl';
import { DescriptionFormat } from '../DescriptionFormat';
import './bannerIntroSection.css';

const descriptionTags = ['p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ul', 'ol', 'li', 'a', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sub', 'sup', 'pre', 'code', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td'];
const sanitizeDescription = rehypeSanitize({
  tagNames: descriptionTags,
  attributes: { '*': ['title'], a: ['href'] },
  protocols: { href: ['http', 'https'] },
  strip: ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template'],
});
type DescriptionNode = ReturnType<typeof sanitizeDescription>['children'][number];

function descriptionText(node: DescriptionNode): string {
  if (node.type === 'text') return node.value;
  return node.type === 'element' ? node.children.map(descriptionText).join('') : '';
}

function renderDescriptionNode(node: DescriptionNode, index: number): ReactNode {
  if (node.type === 'text') return node.value;
  if (node.type !== 'element') return null;
  const properties = { ...node.properties, key: index };
  if (node.tagName === 'a') {
    const href = normalizePublicLandingUrl(node.properties.href);
    return createElement('a', {
      key: index,
      title: node.properties.title,
      ...(href ? {
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
        'aria-label': `${descriptionText(node).trim() || href} (opens in a new tab)`,
      } : {}),
    }, ...node.children.map(renderDescriptionNode));
  }
  return createElement(node.tagName, properties, ...node.children.map(renderDescriptionNode));
}

// Stable renderer identities keep an inline link mounted when focus expands
// the description or another card state changes.
function MarkdownDescriptionLink({ node }: { node: DescriptionNode }) {
  return <>{renderDescriptionNode(node, 0)}</>;
}

// The sanitizer compares allowed protocols case-sensitively. Normalize only
// literal HTTP(S) scheme bytes; keep the rest of the destination unchanged.
function normalizeDescriptionLinkSchemes(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  const current = node as {
    type?: unknown;
    tagName?: unknown;
    properties?: Record<string, unknown>;
    children?: unknown;
  };
  if (current.type === 'element' && current.tagName === 'a'
    && current.properties && typeof current.properties === 'object'
    && !Array.isArray(current.properties)) {
    const href = current.properties.href;
    if (typeof href === 'string') {
      current.properties.href = href.replace(/^https?:/i, scheme => scheme.toLowerCase());
    }
  }
  if (Array.isArray(current.children)) current.children.forEach(normalizeDescriptionLinkSchemes);
}

const descriptionRemarkOptions: Parameters<typeof useRemarkSync>[1] = {
  rehypePlugins: [() => (tree) => {
    normalizeDescriptionLinkSchemes(tree);
    return sanitizeDescription(tree as unknown as Parameters<typeof sanitizeDescription>[0]) as unknown as typeof tree;
  }],
  rehypeReactOptions: {
    passNode: true,
    components: { a: MarkdownDescriptionLink },
  },
};

type BannerIntroSectionProps = {
  officeLogoSrc: string;
  metrcWordmarkSrc: string;
  externalLinkIconSrc: string;
  stateWebsiteHref: string;
  description?: string;
  descriptionFormat?: DescriptionFormat;
  belowHeaderSlot?: ReactNode;
  verifiedBadgeSrc?: string;
  showHeader?: boolean;
};

export default function BannerIntroSection({
  officeLogoSrc,
  metrcWordmarkSrc,
  externalLinkIconSrc,
  stateWebsiteHref,
  description,
  descriptionFormat = DescriptionFormat.PlainText,
  belowHeaderSlot,
  verifiedBadgeSrc,
  showHeader = true,
}: BannerIntroSectionProps) {
  const [logoError, setLogoError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const descRef = useRef<HTMLDivElement>(null);
  const descriptionId = useId();
  const isMarkdown = descriptionFormat === DescriptionFormat.Markdown;
  const markdownDescription = useRemarkSync(isMarkdown ? description || '' : '', descriptionRemarkOptions);
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    // line-height is 24px, 3 lines = 72px; allow 1px tolerance
    setNeedsReadMore(el.scrollHeight > 73);
  }, [description, descriptionFormat]);

  return (
    <>
      {showHeader && <header className="rid-expanded__banner" data-node-id="4224:18146" data-testid="landing-banner">
        {officeLogoSrc && !logoError && <img className="rid-expanded__banner-logo" src={officeLogoSrc} alt="" onError={() => setLogoError(true)} />}
        {verifiedBadgeSrc && (
          <img
            className="rid-expanded__verified"
            src={verifiedBadgeSrc}
            alt="Tested, regulated, legal"
          />
        )}
        <div className="rid-expanded__metrc-group">
          <img src={metrcWordmarkSrc} alt="metrc retail ID" />
          <div>
            <span className='metrc'>metrc</span>
            <span className='retailId'>retail ID</span>
            <span className='tm'>TM</span>  
          </div>
        </div>
      </header>}

      {belowHeaderSlot}

      <section className="rid-expanded__intro" data-node-id="4265:15874">
        {!showHeader && officeLogoSrc && !logoError && <img className="rid-expanded__banner-logo" src={officeLogoSrc} alt="" onError={() => setLogoError(true)} />}
        {description && (
          <div
            ref={descRef}
            id={descriptionId}
            className={`rid-intro-desc${isMarkdown ? ' rid-intro-desc--rich' : ''}${!isExpanded && needsReadMore ? ' rid-intro-desc--clamped' : ''}`}
            onFocusCapture={() => setIsExpanded(true)}
          >
            {isMarkdown ? markdownDescription : description}
          </div>
        )}
        {needsReadMore && (
          <button
            type="button"
            className="rid-intro-read-more"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
            aria-controls={descriptionId}
            data-testid="landing-banner-read-more"
            data-expanded={isExpanded}
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </button>
        )}
        {stateWebsiteHref && (
          <a href={stateWebsiteHref} target="_blank" rel="noreferrer" data-testid="landing-banner-state-website">
            Visit State Website
            <img src={externalLinkIconSrc} alt="" aria-hidden="true" />
          </a>
        )}
      </section>
    </>
  );
}
